import React from 'react';

const AppStoreSecurityPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Account and Data Deletion
          </h1>
          <p className="text-xl text-gray-700">
            Information about deleting your BlueCMR account
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <h2>Application Information</h2>
            <p>
              This policy applies to the <strong>BlueCMR</strong> mobile application, developed by <strong>BLUECMR S.L.U.</strong> and available on the Apple App Store.
            </p>

            <h2>How to Request Account Deletion</h2>
            <p>
              If you wish to delete your BlueCMR account and all associated data, please follow these steps:
            </p>

            <ol>
              <li>
                <strong>Send an email</strong> to <a href="mailto:contact@bluecmr.com">contact@bluecmr.com</a>
              </li>
              <li>
                <strong>Email subject:</strong> "Account deletion request"
              </li>
              <li>
                <strong>Include in the message:</strong>
                <ul>
                  <li>Your full name</li>
                  <li>The email address associated with your BlueCMR account</li>
                  <li>Explicit confirmation that you wish to delete your account and all your data</li>
                </ul>
              </li>
              <li>
                <strong>Response time:</strong> We will process your request within a maximum of 30 calendar days from receipt of your email.
              </li>
            </ol>

            <h2>Data That Will Be Deleted</h2>
            <p>
              When you request account deletion, the following data will be permanently removed:
            </p>
            <ul>
              <li><strong>Profile data:</strong> name, surname, email address, phone number, and profile picture</li>
              <li><strong>Authentication data:</strong> login credentials and session tokens</li>
              <li><strong>User preferences:</strong> language settings, notifications, and other personalized preferences</li>
              <li><strong>Usage data:</strong> activity history within the application (except as noted in the following section)</li>
              <li><strong>Technical data:</strong> device identifiers, session logs, and analytics data associated with your account</li>
            </ul>

            <h2>Data That Will Be Retained</h2>
            <p>
              Due to legal, tax, and contractual obligations, certain data must be retained for specific periods:
            </p>

            <h3>1. Signed Transport Documents</h3>
            <p>
              Transport documents (eCMR, Waybill, Control Documents) that you have signed or participated in will be retained for:
            </p>
            <ul>
              <li><strong>Minimum 5 years:</strong> as required by Spanish tax regulations (General Tax Law)</li>
              <li><strong>Up to 10 years:</strong> depending on the document type and applicable sector regulations (dangerous goods transport, food products, etc.)</li>
            </ul>
            <p>
              <strong>Reason for retention:</strong> Compliance with legal obligations regarding tax, commercial, and transport law. These documents have evidentiary value and must be available for administrative and judicial inspections.
            </p>

            <h3>2. Billing Data</h3>
            <p>
              If you have been a paying customer of BlueCMR, we will retain:
            </p>
            <ul>
              <li>Billing information (name, tax ID, fiscal address)</li>
              <li>Issued invoices and accounting records</li>
            </ul>
            <p>
              <strong>Retention period:</strong> 5 years from the date of the last invoice issued, in accordance with current tax regulations.
            </p>

            <h3>3. Data for Legal Defense</h3>
            <p>
              In the event of ongoing claims, litigation, or administrative or judicial proceedings, data necessary for the defense of our legitimate interests will be retained until the final resolution of the proceedings and the expiration of applicable limitation periods.
            </p>

            <h3>4. Anonymized Data</h3>
            <p>
              We may retain fully anonymized statistical and aggregated data (with no possibility of personal identification) for internal analysis, service improvement, and compliance with reporting obligations to competent authorities.
            </p>

            <h2>Effects of Account Deletion</h2>
            <p>
              Once your account is deleted:
            </p>
            <ul>
              <li>You will no longer be able to access the BlueCMR application with your previous credentials</li>
              <li>You will lose access to all documents and data stored in your account (except those retained due to legal obligations)</li>
              <li>You will not be able to recover your account or the deleted data</li>
              <li>If you wish to use BlueCMR again, you will need to create a new account</li>
            </ul>

            <h2>Applicable Regulations</h2>
            <p>
              This data deletion policy is governed by:
            </p>
            <ul>
              <li><strong>Regulation (EU) 2016/679 (GDPR):</strong> General Data Protection Regulation</li>
              <li><strong>Organic Law 3/2018 (LOPDGDD):</strong> Data Protection and Digital Rights Guarantee Law</li>
              <li><strong>Law 58/2003:</strong> General Tax Law (retention of tax documents)</li>
              <li><strong>Commercial Code:</strong> obligations for retention of commercial documentation</li>
              <li><strong>Sector-specific transport regulations:</strong> depending on the type of goods and operation</li>
            </ul>

            <h2>Contact</h2>
            <p>
              For any questions about this policy or the processing of your personal data, you can contact us at:
            </p>
            <ul>
              <li><strong>Email:</strong> <a href="mailto:contact@bluecmr.com">contact@bluecmr.com</a></li>
              <li><strong>Postal address:</strong> Calle Fueros de Leon 1, 24402, Ponferrada, León, Spain</li>
            </ul>

            <p>
              For more information about how we process your personal data, please see our full <a href="/en/privacy">Privacy Policy</a>.
            </p>

            <p><strong>Last updated:</strong> February 2026</p>

          </div>
        </div>
      </section>
    </div>
  );
};

export default AppStoreSecurityPage;
